import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { BookingForm } from "@/components/BookingForm";
import { ProductsSection } from "@/components/ProductsSection";
import {
  Sparkle, Phone, MapPin, WhatsappLogo, Clock, ArrowRight, ArrowDown,
  Prescription, HeartStraight, ChatCircleText, Tag, Ear, Drop, CaretRight,
} from "@phosphor-icons/react";

const PHONE = "+35725753000";
const WHATSAPP = "35799874969";
const MAPS = "https://www.google.com/maps/search/?api=1&query=Marinou%20Geroulanou%2050%2C%20Kato%20Polemidia%2C%20Limassol%2C%20Cyprus";
const STOREFRONT = "https://med-details-1.emergent.host/images/storefront.jpg";
const OFFER_AVENE = "https://med-details-1.emergent.host/api/files/christofi-pharmacy/uploads/956d4424-615c-4986-a97b-5206c502c2b7.jpg";
const OFFER_SIGG = "https://med-details-1.emergent.host/api/files/christofi-pharmacy/uploads/318f857f-f1a5-4c67-9a3e-859faf665666.png";
const SERVICES_IMG = "https://images.pexels.com/photos/7615621/pexels-photo-7615621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const isSummer = (d) => { const m = d.getMonth() + 1; return m >= 5 && m <= 10; };
const computeOpen = () => {
  const d = new Date();
  const day = d.getDay();
  const mins = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return false;
  if (day === 3 || day === 6) return mins >= 480 && mins <= 810;
  if ([1, 2, 4, 5].includes(day)) {
    const summer = isSummer(d);
    const aStart = summer ? 960 : 900;
    const aEnd = summer ? 1170 : 1110;
    return (mins >= 480 && mins <= 810) || (mins >= aStart && mins <= aEnd);
  }
  return false;
};

const SERVICE_ICONS = [Prescription, HeartStraight, ChatCircleText];

export default function PiercingPage() {
  const { t } = useLang();
  const s = t.site;
  const bookingRef = useRef(null);
  const [open] = useState(computeOpen());
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#F1EEE4] font-sans text-[#20261E]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#20261E]/8 bg-[#F1EEE4]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <a href="#top" className="flex items-baseline gap-2">
            <span className="font-serif text-lg font-semibold tracking-tight text-[#24483A]">Michalis</span>
            <span className="font-serif text-lg italic text-[#BC6B43]">Christofi</span>
          </a>
          <nav className="hidden items-center gap-7 text-sm font-medium text-[#5B6053] lg:flex">
            <button onClick={() => scrollTo("services")} className="transition-colors hover:text-[#24483A]">{s.nav.services}</button>
            <button onClick={() => scrollTo("offers")} className="transition-colors hover:text-[#24483A]">{s.nav.offers}</button>
            <button onClick={() => scrollTo("piercing")} className="transition-colors hover:text-[#24483A]">{s.nav.piercing}</button>
            <button onClick={() => scrollTo("hours")} className="transition-colors hover:text-[#24483A]">{s.nav.hours}</button>
            <button onClick={() => scrollTo("contact")} className="transition-colors hover:text-[#24483A]">{s.nav.contact}</button>
          </nav>
          <div className="flex items-center gap-3">
            <span
              data-testid="open-badge"
              className={`hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold sm:inline-flex ${
                open ? "bg-[#24483A] text-[#F1EEE4]" : "bg-[#E7E1D2] text-[#8F8A79]"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${open ? "bg-[#9DBBA9]" : "bg-[#BC6B43]"}`} />
              {open ? s.openNow : s.closedNow}
            </span>
            <a href={`tel:${PHONE}`} data-testid="header-phone" className="hidden items-center gap-1.5 text-sm font-medium text-[#24483A] hover:text-[#163024] sm:flex">
              <Phone size={16} weight="fill" /> {s.contact.phone}
            </a>
            <LanguageToggle />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="mx-auto grid max-w-6xl items-center gap-8 px-5 py-12 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#BC6B43]">{s.hero.eyebrow}</p>
          <h1 className="mt-5 font-serif text-5xl font-medium leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            <span className="block text-[#24483A]">{s.hero.name1}</span>
            <span className="block italic text-[#BC6B43]">{s.hero.name2}</span>
          </h1>
          <p className="mt-6 font-serif text-xl italic text-[#5B6053]">{s.hero.tagline}</p>
          <p className="mt-3 text-sm uppercase tracking-[0.1em] text-[#8F8A79]">{s.hero.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`tel:${PHONE}`} data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#24483A] px-7 py-3.5 font-semibold text-[#F1EEE4] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#163024]">
              <Phone size={17} weight="fill" /> {s.hero.call}
            </a>
            <a href={MAPS} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#24483A]/20 px-7 py-3.5 font-medium text-[#24483A] transition-all duration-200 hover:bg-[#E7E1D2]">
              <MapPin size={17} /> {s.hero.directions}
            </a>
            <button onClick={() => scrollTo("piercing")} data-testid="hero-piercing-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#BC6B43] px-7 py-3.5 font-semibold text-[#F1EEE4] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#A35B36]">
              {s.hero.piercing} <ArrowRight size={16} />
            </button>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
          <img src={STOREFRONT} alt="Michalis Christofi Pharmacy"
            className="aspect-[4/5] w-full rounded-3xl object-cover shadow-[0_16px_48px_rgba(32,38,30,0.18)]" />
        </motion.div>
      </section>

      {/* Offers */}
      <section id="offers" className="mx-auto max-w-6xl px-5 py-12">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#BC6B43]">{s.offers.eyebrow}</p>
        <h2 className="mt-2 font-serif text-3xl tracking-tight sm:text-4xl">{s.offers.title}</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {/* Shoes: no photo per request */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="flex flex-col justify-between rounded-2xl bg-[#24483A] p-7 text-[#F1EEE4] shadow-[0_10px_36px_rgba(32,38,30,0.1)]">
            <Tag size={28} weight="fill" className="text-[#F1EEE4]/70" />
            <div className="mt-10">
              <h3 className="font-serif text-2xl leading-tight">{s.offers.items[0].title}</h3>
              <p className="mt-3 text-sm text-[#F1EEE4]/75">{s.offers.items[0].desc}</p>
            </div>
          </motion.div>
          {[{ img: OFFER_AVENE, item: s.offers.items[1] }, { img: OFFER_SIGG, item: s.offers.items[2] }].map((o, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 * (i + 1) }}
              className="overflow-hidden rounded-2xl border border-[#20261E]/8 bg-[#FBFAF4] shadow-[0_10px_36px_rgba(32,38,30,0.06)]">
              <div className="aspect-[4/3] overflow-hidden bg-[#E7E1D2]">
                <img src={o.img} alt={o.item.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl leading-tight text-[#20261E]">{o.item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5B6053]">{o.item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="mx-auto max-w-6xl px-5 py-12">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:items-center">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#BC6B43]">{s.services.eyebrow}</p>
            <h2 className="mt-2 font-serif text-3xl tracking-tight sm:text-4xl">{s.services.title}</h2>
            <img src={SERVICES_IMG} alt="Services" className="mt-6 hidden h-64 w-full rounded-2xl object-cover lg:block" />
          </div>
          <div className="space-y-4">
            {s.services.items.map((it, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <motion.div key={i} initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-5 rounded-2xl border border-[#20261E]/8 bg-[#FBFAF4] p-6 shadow-[0_8px_28px_rgba(32,38,30,0.05)]">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#E7E1D2] text-[#24483A]">
                    <Icon size={24} weight="duotone" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-sm text-[#BC6B43]">0{i + 1}</span>
                      <h3 className="font-serif text-xl tracking-tight text-[#20261E]">{it.title}</h3>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-[#5B6053]">{it.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Piercing */}
      <section id="piercing" className="bg-[#24483A] py-16 text-[#F1EEE4]">
        <div className="mx-auto max-w-6xl px-5">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#E7B99C]">{s.piercingSection.eyebrow}</p>
          <h2 className="mt-2 font-serif text-4xl tracking-tight sm:text-5xl">{s.piercingSection.title}</h2>
          <p className="mt-4 max-w-xl text-[#F1EEE4]/75">{s.piercingSection.lead}</p>

          <div className="mt-10 grid gap-6 md:grid-cols-12">
            <div className="rounded-2xl bg-[#F1EEE4]/[0.06] p-8 backdrop-blur md:col-span-7">
              <h3 className="font-serif text-2xl tracking-tight">{t.info.procedureTitle}</h3>
              <p className="mt-3 leading-relaxed text-[#F1EEE4]/75">{t.info.procedureBody}</p>
              <div className="mt-6 flex items-center gap-3">
                <HeartStraight size={22} weight="fill" className="text-[#E7B99C]" />
                <h4 className="font-serif text-xl">{t.info.aftercareTitle}</h4>
              </div>
              <p className="mt-2 leading-relaxed text-[#F1EEE4]/75">{t.info.aftercareBody}</p>
            </div>
            <div className="rounded-2xl bg-[#BC6B43] p-8 md:col-span-5">
              <Drop size={26} weight="fill" className="text-[#F1EEE4]/80" />
              <h3 className="mt-3 font-serif text-2xl tracking-tight">{t.info.pricesTitle}</h3>
              <div className="mt-5 space-y-4">
                <div className="flex items-baseline justify-between border-b border-[#F1EEE4]/25 pb-3">
                  <span>{t.info.priceEar} <span className="text-[#F1EEE4]/70">{t.info.priceEarUnit}</span></span>
                  <span className="font-serif text-2xl font-semibold">€5</span>
                </div>
                <div className="flex items-baseline justify-between border-b border-[#F1EEE4]/25 pb-3">
                  <span>{t.info.priceNose}</span>
                  <span className="font-serif text-2xl font-semibold">€10</span>
                </div>
              </div>
              <p className="mt-5 text-sm text-[#F1EEE4]/75">{t.info.pricesNote}</p>
              <button onClick={() => scrollTo("booking")} data-testid="piercing-book-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F1EEE4] px-6 py-3 font-semibold text-[#24483A] transition-all duration-200 hover:-translate-y-[1px]">
                {s.piercingSection.cta} <ArrowDown size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking */}
      <section id="booking" ref={bookingRef} className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="mb-8 font-serif text-3xl tracking-tight sm:text-4xl">{t.form.title}</h2>
        <BookingForm />
      </section>

      {/* Products catalog */}
      <ProductsSection />

      {/* Hours */}
      <section id="hours" className="mx-auto max-w-6xl px-5 py-12">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#BC6B43]">{s.hoursSection.eyebrow}</p>
        <h2 className="mt-2 font-serif text-3xl tracking-tight sm:text-4xl">{s.hoursSection.title}</h2>
        <p className="mt-2 text-sm text-[#8F8A79]">{s.hoursSection.note}</p>
        <div className="mt-8 overflow-hidden rounded-2xl border border-[#20261E]/8 bg-[#FBFAF4]">
          {s.hoursSection.rows.map((r, i) => (
            <div key={i} className="flex flex-col gap-1 border-b border-[#20261E]/8 px-6 py-5 last:border-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-[#24483A]" />
                <span className="font-medium text-[#20261E]">{r.days}</span>
              </div>
              <span className="whitespace-pre-line text-sm text-[#5B6053] sm:text-right">{r.hours}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="mx-auto max-w-6xl px-5 py-12">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#BC6B43]">{s.contact.eyebrow}</p>
        <h2 className="mt-2 font-serif text-3xl tracking-tight sm:text-4xl">{s.contact.title}</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-[#20261E]/8 bg-[#FBFAF4] p-7">
            <MapPin size={26} weight="duotone" className="text-[#24483A]" />
            <h3 className="mt-3 text-xs uppercase tracking-[0.1em] text-[#8F8A79]">{s.contact.addressLabel}</h3>
            <p className="mt-1 leading-relaxed text-[#20261E]">{s.contact.address}</p>
            <a href={MAPS} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#BC6B43] hover:underline">
              {s.contact.maps} <CaretRight size={14} />
            </a>
          </div>
          <div className="rounded-2xl border border-[#20261E]/8 bg-[#FBFAF4] p-7">
            <Phone size={26} weight="duotone" className="text-[#24483A]" />
            <h3 className="mt-3 text-xs uppercase tracking-[0.1em] text-[#8F8A79]">{s.contact.phoneLabel}</h3>
            <a href={`tel:${PHONE}`} className="mt-1 block font-serif text-2xl text-[#24483A] hover:text-[#163024]">{s.contact.phone}</a>
          </div>
          <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
            className="flex flex-col justify-between rounded-2xl bg-[#24483A] p-7 text-[#F1EEE4] transition-all duration-200 hover:-translate-y-[2px]">
            <WhatsappLogo size={30} weight="fill" className="text-[#9DBBA9]" />
            <div className="mt-8">
              <h3 className="font-serif text-2xl">{s.contact.whatsapp}</h3>
              <p className="mt-1 text-sm text-[#F1EEE4]/75">+357 99 874969</p>
            </div>
          </a>
        </div>
      </section>

      <footer className="border-t border-[#20261E]/10 py-8 text-center text-sm text-[#8F8A79]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-4 px-5">
          <span>Michalis Christofi Pharmacy · {new Date().getFullYear()}</span>
          <Link data-testid="admin-link" to="/piercing/admin" className="hover:text-[#24483A]">{s.nav.admin}</Link>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_24px_rgba(37,211,102,0.45)] transition-transform duration-200 hover:scale-105">
        <WhatsappLogo size={30} weight="fill" />
      </a>
    </div>
  );
}
